import { CASH_CROP_PLANTING_RATE_UNIT, RDCashCropInput } from '@cibo/core'
import { DatePickerField, NumberInputField, SelectField } from '@cibo/ui'
import { Stack } from '@mui/material'
import InputLabel from '@mui/material/InputLabel'
import { useFormikContext } from 'formik'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { DetailEditorPropsByYear } from '../types'
import { useIsRequired } from '../useIsRequired'
import { useShowDetailAttribute } from '../useShowDetailAttribute'
import { PlantingRateWarning } from './PlantingRateWarning'
import { CashCropEditorInputs } from './types'

const ConditionallyShow = ({ show, children }: { show: boolean; children: ReactNode }) =>
  show ? <>{children}</> : <></>

const findDefaultPlantingMonth = (year: number, crop: string) =>
  new Date(year, crop === 'corn' ? 3 : 4)

export const CashCropPlantingInputs = ({
  filterInputs,
  name = 'cashCropEditor',
  requirement,
  year,
}: DetailEditorPropsByYear) => {
  const { t } = useTranslation('@cibo/landmanager/CashCropEditor')

  const { getFieldMeta } = useFormikContext<CashCropEditorInputs>()
  const meta = getFieldMeta<CashCropEditorInputs>('input')
  const { crop, plantingRate, plantingRateUnit, source } = meta.value || {}
  const isCiboSourced = (subField: keyof RDCashCropInput) =>
    !!meta.initialValue?.[subField] &&
    source === 'cibo' &&
    meta.initialValue?.[subField] === meta.value[subField]

  const defaultPlantingMonth = findDefaultPlantingMonth(year, crop)

  const benchmarkRequires = useIsRequired(requirement)
  const show = useShowDetailAttribute<RDCashCropInput>(filterInputs)

  return (
    <Stack spacing={1}>
      <ConditionallyShow show={show('plantedAt')}>
        <Stack>
          <InputLabel required={!!benchmarkRequires('plantedAt')} htmlFor={`${name}.plantedAt`}>
            {t('planted')}
          </InputLabel>
          <DatePickerField
            name={`${name}.plantedAt`}
            inlineClearButton
            ciboSourced={isCiboSourced('plantedAt')}
            minDate={new Date(year, 0, 1)}
            maxDate={new Date(year + 1, 0, 1)}
            referenceDate={defaultPlantingMonth}
            data-testid={`cashCrop.${year}.plantedAt`}
            stringValue
          />
        </Stack>
      </ConditionallyShow>

      <ConditionallyShow show={show('plantingRate') || show('plantingRateUnit')}>
        <Stack direction="row" spacing={1}>
          <ConditionallyShow show={show('plantingRate')}>
            <Stack sx={{ flex: 0.75, minWidth: '105px' }}>
              <InputLabel
                required={!!benchmarkRequires('plantingRate')}
                htmlFor={`${name}.plantingRate`}
              >
                {t('plantingRate')}
              </InputLabel>
              <NumberInputField
                name={`${name}.plantingRate`}
                data-testid={`cashCrop.${year}.plantingRate`}
              />
              <PlantingRateWarning
                plantingRate={plantingRate}
                plantingRateUnit={plantingRateUnit}
                crop={crop}
              />
            </Stack>
          </ConditionallyShow>
          <ConditionallyShow show={show('plantingRateUnit')}>
            <Stack sx={{ flex: 1, minWidth: 0 }}>
              <InputLabel
                required={!!benchmarkRequires('plantingRateUnit')}
                htmlFor={`${name}.plantingRateUnit`}
              >
                {t('unit')}
              </InputLabel>

              <SelectField
                fieldName={`${name}.plantingRateUnit`}
                options={CASH_CROP_PLANTING_RATE_UNIT}
                renderOption={context => t('plantingRate', { context })}
                data-testid={`cashCrop.${year}.plantingRateUnit`}
              />
            </Stack>
          </ConditionallyShow>
        </Stack>
      </ConditionallyShow>
    </Stack>
  )
}
